/**
 * IS_PREVIEW fixtures for `/join` and `/join/offer` (plan 476 M3): the page
 * renders with no Clerk and no network. `?join=<state>` picks the state:
 *
 *   form          the open-lane form (default)
 *   refusal       a validate refusal already shown inline
 *   code          the one-time-code step (any code but 424242 is refused)
 *   out_of_area   submitting succeeds and lands on Home's out-of-area state
 *   policy_retry  the policy read failed: retry state, no code field
 *   codeonly      the invite-code-required form
 *
 * Nothing here runs outside IS_PREVIEW.
 */
import { ApiError } from "./api";
import type { JoinApi, JoinAuth } from "./join-api";
import { previewQueryParam } from "./pro-mode";

export type JoinPreviewState =
  | "form"
  | "refusal"
  | "code"
  | "out_of_area"
  | "policy_retry"
  | "codeonly";

const STATES: readonly JoinPreviewState[] = [
  "form",
  "refusal",
  "code",
  "out_of_area",
  "policy_retry",
  "codeonly",
];

export function previewJoinState(): JoinPreviewState {
  const raw = previewQueryParam("join")?.trim().toLowerCase();
  return (STATES as readonly string[]).includes(raw ?? "") ? (raw as JoinPreviewState) : "form";
}

export const PREVIEW_JOIN_CODE = "424242";

export function previewJoinApi(state: JoinPreviewState): JoinApi {
  return {
    validate: async (req) => {
      if (state === "refusal") {
        throw new ApiError(409, "conflict", { code: "professional_credential_conflict" });
      }
      return {
        valid: true,
        service_area_label: req.service_area,
        service_area_launched: state === "out_of_area" ? false : true,
      };
    },
    join: async (req) => ({
      redemption_id: "preview-redemption",
      status: "provisioned",
      pro_type: req.pro_type ?? "agent",
      service_area: req.service_area,
      service_area_launched: state === "out_of_area" ? false : true,
      created: true,
      lane: "open",
    }),
    policy: async () => {
      if (state === "policy_retry") throw new Error("preview: policy read failed");
      return {
        policy: state === "codeonly" ? "code_only" : "open",
        code_required: state === "codeonly",
      };
    },
    requirements: async () => ({
      credential_required: true,
      credential_kind: "re_license",
      pro_type: "agent",
      license_state_required: true,
    }),
    role: async () => null,
  };
}

export const previewJoinAuth: JoinAuth = {
  isLoaded: true,
  isSignedIn: false,
  getToken: async () => "preview-token",
  startSignUp: async (input) =>
    input.email.toLowerCase().startsWith("exists@") ? { kind: "exists" } : { kind: "code_sent" },
  startSignIn: async () => ({ kind: "code_sent" }),
  verify: async (_mode, code) =>
    code === PREVIEW_JOIN_CODE
      ? { ok: true }
      : { ok: false, message: "That code isn't right. Check it and try again." },
};
